import {useEffect, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {PATHS} from 'routes/constants';
import {PATHS as CLASSIC_PATHS} from '@legacy/routes/constants';
import {LAYOUT_MODE_KEY} from 'utils/constants';
import {getFeatureFlags, LocalStorage, SHORT_LINK_TYPE} from '@chekin/core';
import {useKioskoConnectionStatus} from 'context/kiosko/hooks/useKioskoConnectionStatus';
import {isUnattendedTerminal} from 'hooks/terminalMode';

const getDesignPaths = () =>
  getFeatureFlags('GUESTAPP_REDESIGN_ENABLED') ? PATHS : CLASSIC_PATHS;

const getHousingCheckinOnlineInitPatch = (token = '', isTerminal: boolean) => {
  const paths = getDesignPaths();
  const path = isTerminal
    ? `${token}/${paths.searchReservation.welcomeScreen}`
    : `${token}/${paths.searchReservation.main}`;
  return path;
};

const getDirectReservationPath = (token = '', search = '') => {
  return `${token}/${getDesignPaths().searchReservation.directReservation}${search}`;
};

const getUrlPathsFromShortLink = (
  type = '',
  token = '',
  isTerminal: boolean,
  search = '',
) => {
  const paths = getDesignPaths();

  return {
    [SHORT_LINK_TYPE.main]: '',
    [SHORT_LINK_TYPE.payment]: `${token}/${paths.payments.main}`,
    [SHORT_LINK_TYPE.upselling]: `${token}/${paths.recommendations.main}`,
    [SHORT_LINK_TYPE.lock]: `${token}/${paths.remoteAccess}`,
    [SHORT_LINK_TYPE.ivHousing]: `${token}/${paths.verifyOnHousing}`,
    [SHORT_LINK_TYPE.housingCheckinOnline]: getHousingCheckinOnlineInitPatch(
      token,
      isTerminal,
    ),
    [SHORT_LINK_TYPE.housingGuidebook]: `${token}/${paths.housingGuidebooks}`,
    [SHORT_LINK_TYPE.housingDirectReservation]: getDirectReservationPath(token, search),
  }[type];
};

const getIsKioskoTerminal = (isKioskoAttempted: boolean, isKioskoConnected: boolean) => {
  const storedLayout = LocalStorage.get<string>(LAYOUT_MODE_KEY);
  return storedLayout === 'kiosko' || (isKioskoAttempted && isKioskoConnected);
};

type UseRedirectToUrlFromShortLinkProps = {
  linkType?: string;
  token?: string;
};

function useRedirectToUrlFromShortLink({
  linkType,
  token,
}: UseRedirectToUrlFromShortLinkProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const hasBeenRedirected = useRef(false);
  const {
    isConnected: isKioskoConnected,
    hasCompletedInitialization,
    isAttempted: isKioskoAttempted,
  } = useKioskoConnectionStatus();

  const isTerminal =
    getIsKioskoTerminal(isKioskoAttempted, isKioskoConnected) || isUnattendedTerminal();

  useEffect(
    function redirectToUrlFromShortLink() {
      if (hasBeenRedirected.current) return;
      if (isKioskoAttempted && !hasCompletedInitialization) return;

      const redirectUrl = getUrlPathsFromShortLink(
        linkType,
        token,
        isTerminal,
        location.search,
      );

      if (!redirectUrl) return;

      const target = `/${redirectUrl}`;
      const isDeepLinkUnderTarget =
        location.pathname === target || location.pathname.startsWith(`${target}/`);
      if (isDeepLinkUnderTarget) {
        hasBeenRedirected.current = true;
        return;
      }

      navigate(redirectUrl, {replace: true});
      hasBeenRedirected.current = true;
    },
    [
      token,
      linkType,
      navigate,
      isTerminal,
      location.pathname,
      location.search,
      isKioskoAttempted,
      hasCompletedInitialization,
    ],
  );
}

export {useRedirectToUrlFromShortLink};
